/**
 * Las alertas del Resumen, mientras el motor de §7.5 no exista.
 *
 * El motor determinístico de §7.5 --reglas guardadas, cron de madrugada,
 * notificaciones-- NO está construido en el backend, y aquí no se simula: no
 * hay datos inventados ni endpoints nuevos. Lo que sí se puede hacer hoy es
 * OBSERVAR, que es exactamente lo que §7.3.5 dice de los huecos y los solapes:
 * no se declaran, se miran con las consultas que ya existen.
 *
 * Cuatro orígenes, todos del catálogo:
 *
 * | Señal                               | Consulta                   |
 * |-------------------------------------|----------------------------|
 * | vinculados sin ningún resultado     | `get_period_coverage`      |
 * | el mismo KPI en más de un lote      | `get_period_coverage`      |
 * | lote listo esperando revisión       | `find_calculation_batches` |
 * | mecánica del mes sin publicar       | `list_mechanics`           |
 *
 * Todas entran por la MISMA forma --severidad, texto y la evidencia que lo
 * respalda--, y esa forma es lo que importa: el día que exista el motor de
 * §7.5 se le añade un origen y la pantalla no se toca. Por eso `deriveAlerts`
 * es una función pura sobre lo ya consultado y no un hook: lo que hay que
 * poder probar es la regla, no el renderizado.
 *
 * §7.5 exige que cada hallazgo lleve la consulta que lo respalda, así que la
 * evidencia NO es opcional: o lleva a la pantalla que lo enseña, o salta al
 * chat con la pregunta ya escrita.
 */
import { ALERT_NAMES_PREVIEW } from "../lib/limits.ts";

import type { MechanicSummary } from "../mechanics/mechanics.ts";
import type { BatchSummary, PeriodCoverage } from "./batches.ts";

/**
 * Dos severidades y no cinco: `warning` es algo que probablemente esté mal,
 * `info` es algo que está esperando a que alguien lo mire. Una escala más
 * fina obligaría a decidir en qué peldaño va cada hallazgo sin que la
 * decisión cambie nada de lo que el usuario hace después.
 */
export type AlertSeverity = "warning" | "info";

/** Las pantallas a las que puede llevar una evidencia. */
export type AlertTarget =
  | { readonly screen: "batch"; readonly batchId: string }
  | {
      readonly screen: "mechanic";
      readonly period: string;
      readonly slug: string;
    };

/**
 * Cómo se comprueba el hallazgo.
 *
 * `query` es el nombre de la consulta del catálogo que lo respalda y se
 * enseña: es lo que convierte "18 sin resultado" en algo verificable y no en
 * un número que hay que creerse.
 */
export type AlertEvidence =
  | {
      readonly kind: "open";
      readonly query: string;
      readonly label: string;
      readonly target: AlertTarget;
    }
  | {
      readonly kind: "ask";
      readonly query: string;
      readonly label: string;
      /** La pregunta que se le deja escrita al agente. */
      readonly question: string;
    };

export interface Alert {
  readonly id: string;
  readonly severity: AlertSeverity;
  readonly title: string;
  /** El detalle que hace falta para entenderla: los nombres, los totales. */
  readonly detail: string | null;
  readonly evidence: AlertEvidence;
}

export interface Observations {
  readonly period: string;
  readonly coverage?: PeriodCoverage | undefined;
  readonly batches?: readonly BatchSummary[] | undefined;
  readonly mechanics?: readonly MechanicSummary[] | undefined;
}

/** "Ana Pérez, Juan Díaz y 16 más": nombres primero, cuenta después. */
const nameList = (
  names: readonly string[],
  total: number,
  preview = ALERT_NAMES_PREVIEW,
): string => {
  const shown = names.slice(0, preview);
  const rest = total - shown.length;

  if (shown.length === 0) return "";
  if (rest <= 0) return shown.join(", ");
  return `${shown.join(", ")} y ${String(rest)} más`;
};

const plural = (count: number, one: string, many: string): string =>
  `${String(count)} ${count === 1 ? one : many}`;

const missingResults = (coverage: PeriodCoverage): Alert | null => {
  const missing = coverage.enrolled - coverage.withResults;
  if (missing <= 0) return null;

  const names = nameList(
    coverage.missing.map((person) => person.fullName),
    missing,
  );

  return {
    id: "missing-results",
    severity: "warning",
    title: `${plural(missing, "vinculado", "vinculados")} sin ningún resultado este mes`,
    // Los nombres y no solo el número: quien mira esto tiene que poder ir a
    // buscar a esas personas, y "18" no lleva a ninguna parte.
    detail: names === "" ? null : names,
    evidence: {
      kind: "ask",
      query: "get_period_coverage",
      label: "Preguntar quiénes son",
      question:
        `En el periodo ${coverage.period} hay ${String(missing)} personas vinculadas ` +
        `sin ningún resultado. Enséñame quiénes son, con nombre e identificador, ` +
        `y dime de qué mecánica deberían haber salido.`,
    },
  };
};

const duplicatedKpis = (coverage: PeriodCoverage): Alert | null => {
  if (coverage.duplicated.length === 0) return null;

  const people = new Set(coverage.duplicated.map((row) => row.identifier));
  const detail = nameList(
    coverage.duplicated.map(
      (row) =>
        `${row.fullName} · ${row.kpiCode} (${String(row.batches)} lotes)`,
    ),
    coverage.duplicated.length,
  );

  return {
    id: "duplicated-kpi",
    severity: "warning",
    title: `${plural(people.size, "persona", "personas")} con el mismo KPI en varios lotes`,
    detail,
    evidence: {
      kind: "ask",
      query: "get_period_coverage",
      label: "Preguntar qué lotes se pisan",
      question:
        `En el periodo ${coverage.period} hay personas con el mismo KPI en más de un ` +
        `lote. Enséñame quiénes son, qué KPI y qué lotes lo calcularon, y dime cuál ` +
        `debería quedarse.`,
    },
  };
};

/**
 * Una mecánica sin versión publicada no corre cuando nadie dice cuál (§7.3.5),
 * así que un mes entero puede quedarse sin calcular por esto.
 */
const unpublishedMechanics = (
  period: string,
  mechanics: readonly MechanicSummary[],
): readonly Alert[] =>
  mechanics
    .filter(
      (mechanic) =>
        mechanic.period === period && mechanic.publishedNumber === null,
    )
    .map((mechanic) => ({
      id: `unpublished-mechanic:${mechanic.address}`,
      severity: "warning" as const,
      title: `La mecánica ${mechanic.address} no tiene versión vigente`,
      detail:
        mechanic.versionCount === 0
          ? "Todavía no tiene ninguna versión escrita."
          : `${plural(mechanic.versionCount, "versión", "versiones")}, ninguna publicada.`,
      evidence: {
        kind: "open",
        query: "list_mechanics",
        label: "Abrir el documento",
        target: {
          screen: "mechanic",
          period: mechanic.period,
          slug: mechanic.slug,
        },
      },
    }));

/** Un lote en `READY` está esperando a que una persona lo revise. */
const readyBatches = (batches: readonly BatchSummary[]): readonly Alert[] =>
  batches
    .filter((batch) => batch.status === "READY")
    .map((batch) => ({
      id: `ready-batch:${batch.id}`,
      severity: "info" as const,
      title: `El lote ${batch.label} está listo para revisión`,
      detail:
        `${plural(batch.resultCount, "resultado", "resultados")} · ` +
        `${plural(batch.participantCount, "persona", "personas")} · ` +
        `${batch.totalPoints.toLocaleString("es")} puntos`,
      evidence: {
        kind: "open",
        query: "find_calculation_batches",
        label: "Abrir el lote",
        target: { screen: "batch", batchId: batch.id },
      },
    }));

const SEVERITY_ORDER: Record<AlertSeverity, number> = { warning: 0, info: 1 };

/**
 * Lo que hay que mirar hoy, de lo más raro a lo que solo espera.
 *
 * Lo que falta por consultar simplemente no aporta alertas: la banda es útil
 * desde la primera respuesta que llega y no espera a tenerlo todo.
 */
export const deriveAlerts = ({
  period,
  coverage,
  batches,
  mechanics,
}: Observations): readonly Alert[] => {
  const alerts: Alert[] = [];

  if (coverage !== undefined) {
    const missing = missingResults(coverage);
    if (missing !== null) alerts.push(missing);

    const duplicated = duplicatedKpis(coverage);
    if (duplicated !== null) alerts.push(duplicated);
  }

  if (mechanics !== undefined) {
    alerts.push(...unpublishedMechanics(period, mechanics));
  }

  if (batches !== undefined) {
    alerts.push(...readyBatches(batches));
  }

  // Estable: dentro de la misma severidad manda el orden en que se añadieron,
  // que es el orden en que se leen.
  return alerts.sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );
};
